import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Typography, FormControlLabel, Checkbox, Snackbar, Alert, CircularProgress } from '@mui/material';
import InputField from '../InputField/InputField';
import { useLanguage } from './../../context/LanguageContext/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { login } from '../../redux/slices/loginSlice';
import { setSuccessMessage } from '../../redux/slices/successMessageSlice';
import PhoneNumberField from "../InputField/PhoneNumberField";
import { signInValidationSchema } from '../../utils/validation';
import { useFormik } from 'formik';

const SignInForm = ({ customHandleChange, t, direction, onForgotPassword }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isLoggedIn, loading, error, token } = useSelector((state) => state.login);
  const [rememberMe, setRememberMe] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Controls Snackbar visibility
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const formik = useFormik({
    initialValues: {
        phone: "",
        password: "",
    },
    validationSchema: signInValidationSchema(t),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      
    },
});

  const handleLogin = (values) => {
 
    const loginData = {
      username: values.phone,
      password: values.password,
      rememberMe,
    };
    dispatch(login({loginData,language}))
      .unwrap()
      .then((response) => {

        if (response.token) {
          dispatch(setSuccessMessage({
            message: t.loginSuccess,
          }));
          navigate('/', { state: { successMessage: t.loginSuccess } });
        } else if (response.apiresponseresult.successtate === 303) {
          setSnackbarMessage( response.apiresponseresult.errormessage);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      })

      .catch((err) => {
        setSnackbarMessage(err );
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  };



  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
     
      <PhoneNumberField
        formik={formik}
        customHandleChange={customHandleChange}
        t={t}
        direction={direction}
      />
           

      {/* Password Field */}
      <InputField
        label={t.password}
        type="password"
        name="password"
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        placeholder={t.enterPassword}
        error={formik.touched.password && Boolean(formik.errors.password)}
        helperText={formik.touched.password && formik.errors.password}
        required
        direction={direction}
      />
      
      {/* Remember Me and Forgot Password */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: direction === "rtl" ? 'row-reverse' : 'row',
          // Use Material-UI's breakpoints for responsive styling
          flexDirection: "row"
        }}
      >
        <FormControlLabel
          control={<Checkbox
            checked={rememberMe}
            onChange={() => setRememberMe(!rememberMe)}
          />}
          label={t.rememberMe}
          sx={{
            marginRight: direction === "rtl" ? 0 : undefined,
            marginLeft: direction === "rtl" ? undefined : 0,
            flexDirection: direction === "rtl" ? 'row-reverse' : 'row',
            fontSize: {
              xs: '0.75rem',  // Smaller font size for extra-small screens
              sm: '0.875rem', // Medium font size for small screens
              lg: '1rem',     // Larger font size for large screens
            },
          }}
        />
        <Typography
          sx={{
            color: '#0C2A92',
            cursor: 'pointer',
            fontSize: {
              xs: '0.75rem',  // Smaller font size for extra-small screens
              sm: '0.875rem', // Medium font size for small screens
              lg: '1rem',     // Larger font size for large screens
            },
            fontWeight: 'bold',
            textAlign: direction === "rtl" ? 'left' : 'right',
          }}
          onClick={onForgotPassword}
        >
          {t.forgotPassword}
        </Typography>
      </Box>


      {/* Login Button */}
      <Button
        variant="contained"
        fullWidth
        sx={{
          padding: '10px',
          fontSize: '16px',
          fontWeight: 600,
          backgroundColor: '#0E518A',
          '&:hover': { backgroundColor: '#072138' },
          textTransform: 'none',
          mt: 2,
        }}
        type="submit"
        onClick={() => handleLogin(formik.values)} // Trigger login on button click
        disabled={loading || !!formik.errors.password || !!formik.errors.phone}
      >
        {loading ? (
          <CircularProgress size={24} sx={{ color: 'white' }} /> // White spinner
        ) : (
          t.login // Show login text when not loading
        )}
      </Button>
      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SignInForm;
